import React, { useEffect, useState } from 'react';
import { Flex, Typography, Button, Form, Input, Alert } from 'antd';
import { Rule } from 'antd/es/form';
import { useNavigate, NavLink } from 'react-router-dom';
import { GoogleCircleFilled } from '@ant-design/icons';
import { UserAuth } from '../../../middleware/UserContext/UserContext.middleware';
import { createUser } from '../../../infrastructure/http/AuthAPI';
import { UserCredential, AuthError } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, currentUser, logout } = UserAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { t } = useTranslation();

  const emailValidationRules: Rule[] = [
    { required: true, message: t('email-required', { ns: 'validations' }) },
    { type: 'email', message: t('email-valid', { ns: 'validations' }) }
  ];
  
  const passwordValidationRules: Rule[] = [
    { required: true, message: t('password-required', { ns: 'validations' }) }
  ];

  const onLogin = (values: { email: string; password: string }) => {
    setLoading(true);
    setErrorMessage(null);
    login(values.email, values.password)
      .catch((error: AuthError) => {
        setErrorMessage(t(error.code, { ns: 'errors' }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onClickGoogleLogin = async () => {
    setGoogleLoading(true);
    setErrorMessage(null);
    loginWithGoogle()
      .then((uc: UserCredential) => {
        return createUser(uc); 
      })
      .catch((error: AuthError) => {
        setErrorMessage(t(error.code, { ns: 'errors' }));
        logout();
      })
      .finally(() => {
        setGoogleLoading(false);
      });
  };

  useEffect(() => {
    if (currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  return (
    <Flex vertical align="center" justify="center">
      <Typography.Title level={3}>{t('login-title', { ns: 'auth' })}</Typography.Title>
      {errorMessage && (
        <Alert 
          message={errorMessage} 
          type="error" 
          showIcon 
          style={{ marginBottom: '16px', width: '100%', maxWidth: '400px' }} 
        />
      )}
      <Form
        name="login_form"
        initialValues={{ remember: true }}
        autoComplete="off"
        onFinish={onLogin}
        style={{ width: '100%', maxWidth: '400px' }}
        layout='vertical'
      >
        <Form.Item label={t('email', { ns: 'auth' })} name="email" rules={emailValidationRules}>
          <Input placeholder={t('email-placeholder', { ns: 'auth' })} disabled={loading || googleLoading} />
        </Form.Item>

        <Form.Item
          label={t('password', { ns: 'auth' })}
          name="password"
          rules={passwordValidationRules}
        >
          <Input.Password placeholder={t('password-placeholder', { ns: 'auth' })} disabled={loading || googleLoading} />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading} disabled={googleLoading}>
            {t('login-button', { ns: 'auth' })}
          </Button>
          <Button 
            onClick={onClickGoogleLogin} 
            icon={<GoogleCircleFilled />} 
            block 
            style={{ marginTop: '16px' }} 
            loading={googleLoading}
            disabled={loading}
          >
            {t('signup-with-google', { ns: 'auth' })}
          </Button>
        </Form.Item>
      </Form> 
      <Typography.Paragraph style={{textAlign: 'center', marginTop: '16px'}} > 
        {t('not-registered', { ns: 'auth' })}{' '}
        <NavLink to='/sign_up'>
          {t('signup-link', { ns: 'auth' })}
        </NavLink>
      </Typography.Paragraph>
    </Flex>
  );
};

export default LoginPage;